<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card">
      <div class="page-desc">{{ $t('booksWithMaterials.pageDesc') }}</div>
      <el-form :model="query" :inline="true" class="glass-form">
        <el-form-item :label="$t('common.consumptionDate')">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            size="small"
            style="width: 280px"
            :range-separator="$t('common.dateRangeSep')"
            :start-placeholder="$t('common.dateStart')"
            :end-placeholder="$t('common.dateEnd')"
            value-format="yyyy-MM-dd"
            :picker-options="datePickerOptions"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item :label="$t('booksWithMaterials.accountId')">
          <el-input
            v-model="query.adAccountId"
            :placeholder="$t('booksWithMaterials.accountIdPlaceholder')"
            clearable
            size="small"
            style="width: 200px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>

      <el-table
        v-loading="loading"
        :data="tableData"
        border
        size="small"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
      >
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column :label="$t('common.bookName')" prop="bookName" min-width="220" show-overflow-tooltip />
        <el-table-column :label="$t('booksWithMaterials.adAccountId')" prop="adAccountId" width="180" show-overflow-tooltip />
        <el-table-column :label="$t('booksWithMaterials.accountName')" prop="accountName" min-width="160" show-overflow-tooltip />
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="160">
          <template slot-scope="{ row }">
            <el-button
              size="mini"
              type="text"
              icon="el-icon-view"
              @click="goToBookSummary(row)"
            >
              {{ $t('booksWithMaterials.viewInSummary') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="total === 0 && !loading" class="empty-tip">
        <el-empty :description="$t('booksWithMaterials.emptyTip')" />
        <div class="empty-help">
          <div class="help-title">{{ $t('booksWithMaterials.helpTitle') }}</div>
          <ul>
            <li>{{ $t('booksWithMaterials.help1') }}</li>
            <li>{{ $t('booksWithMaterials.help2') }}</li>
            <li>{{ $t('booksWithMaterials.help3') }}</li>
            <li>{{ $t('booksWithMaterials.help4') }}</li>
          </ul>
        </div>
      </div>
      <pagination
        v-show="total > 0"
        :total="total"
        :page.sync="pageNo"
        :limit.sync="pageSize"
        @pagination="fetchData"
      />

      <div v-if="total > 0" class="summary-tip">
        {{ $t('booksWithMaterials.resultTip', { total }) }}
      </div>
    </el-card>
  </div>
</template>

<script>
import { queryBooksWithMaterials } from '@/api/report/insights';
import { withLocalDevLongRangeDateShortcuts } from '@/utils/dateRangePickerOptions';
import Pagination from '@/components/Pagination';

export default {
  name: 'BooksWithMaterials',
  components: { Pagination },
  data() {
    return {
      loading: false,
      dateRange: [],
      datePickerOptions: withLocalDevLongRangeDateShortcuts([
        { text: '近7天', onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); picker.$emit('pick', [s, e]); } },
        { text: '近30天', onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); picker.$emit('pick', [s, e]); } },
        { text: '近90天', onClick(picker) { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 89); picker.$emit('pick', [s, e]); } }
      ]),
      query: {
        startDate: undefined,
        endDate: undefined,
        adAccountId: undefined
      },
      pageNo: 1,
      pageSize: 10,
      total: 0,
      tableData: []
    };
  },
  created() {
    this.initDefaultDateRange();
    this.fetchData();
  },
  methods: {
    handleQuery() {
      this.pageNo = 1;
      this.fetchData();
    },
    initDefaultDateRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      const fmt = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      this.dateRange = [fmt(start), fmt(end)];
      this.query.startDate = fmt(start);
      this.query.endDate = fmt(end);
    },
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          startDate: this.query.startDate,
          endDate: this.query.endDate,
          adAccountId: this.query.adAccountId || undefined,
          pageNo: this.pageNo,
          pageSize: this.pageSize
        };
        const res = await queryBooksWithMaterials(params);
        const page = res.data || {};
        this.tableData = page.records || [];
        this.total = page.total != null ? Number(page.total) : 0;
      } catch (e) {
        this.$message.error((e.message || e) + '');
      } finally {
        this.loading = false;
      }
    },
    resetQuery() {
      this.initDefaultDateRange();
      this.query.adAccountId = undefined;
      this.pageNo = 1;
      this.fetchData();
    },
    handleDateChange(val) {
      if (val && val.length === 2) {
        this.query.startDate = val[0];
        this.query.endDate = val[1];
      } else {
        this.query.startDate = undefined;
        this.query.endDate = undefined;
      }
    },
    goToBookSummary(row) {
      const q = {};
      if (row.bookName) q.bookName = row.bookName;
      if (row.adAccountId) q.adAccountId = row.adAccountId;
      if (this.query.startDate) q.startDate = this.query.startDate;
      if (this.query.endDate) q.endDate = this.query.endDate;
      this.$router.push({ path: '/report/book-summary', query: q });
    }
  }
};
</script>

<style scoped>
.page-desc {
  margin-bottom: 16px;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}
.empty-tip {
  padding: 24px 0;
}
.empty-help {
  margin-top: 20px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
  text-align: left;
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
}
.help-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.empty-help ul {
  margin: 0;
  padding-left: 20px;
}
.summary-tip {
  margin-top: 12px;
  color: #909399;
  font-size: 13px;
}
</style>
